import { FC } from 'react';

import {
    CheckCircle,
    MessageSquare,
    Package,
    Pencil,
    Truck,
    X,
} from 'lucide-react';
import { Clock } from 'lucide-react';

import { CancellationDetails } from './CancellationDetails';
import { OrderStatusNotice } from './OrderStatusNotice';
import { StatusNoticeTone } from './orderStatusNoticeTone';
import { OrderTelegramContactNotice } from './OrderTelegramContactNotice';
import { Button } from '../../../components/ui/Button';
import { OrdersService } from '../../../services/OrdersService';
import { Order } from '../../../types';

interface Props {
    order: Order;
    tone: StatusNoticeTone;
    onConfirmReceived: (orderId: number) => void;
    onReviewOrder: (order: Order) => void;
    onCancelOrder: (orderId: number) => void;
    onEditOrder: (order: Order) => void;
}

export const PurchasesStatusPanel: FC<Props> = ({
    order,
    tone,
    onConfirmReceived,
    onReviewOrder,
    onCancelOrder,
    onEditOrder,
}) => {
    return (
        <>
            {order.status === 'awaiting_shipment' && (
                <OrderTelegramContactNotice
                    telegramUsername={order.seller?.telegram_username}
                    message="Зв'яжіться з продавцем через Telegram:"
                    tone={tone}
                />
            )}

            {order.status === 'shipped' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Truck}
                    message={
                        OrdersService.waitingForRecipient(
                            order.status,
                            order.delivery,
                        )
                            ? 'Відправлення прибуло у точку видачі та очікує отримання.'
                            : 'Замовлення відправлено. Статус доставки оновиться автоматично після підтвердження перевізником.'
                    }
                />
            )}

            {order.status === 'handed_over' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Truck}
                    message="Продавець передав вам замовлення. Підтвердіть отримання."
                    action={
                        <Button
                            size="sm"
                            className="self-start bg-green-600 text-white hover:bg-green-700 sm:self-auto"
                            onClick={() => onConfirmReceived(order.id)}
                        >
                            <CheckCircle className="mr-1 h-4 w-4" />
                            Підтвердити отримання
                        </Button>
                    }
                />
            )}

            {(order.status === 'pending' ||
                order.status === 'awaiting_shipment') && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Clock}
                    message={
                        order.status === 'pending'
                            ? 'Замовлення створено. Очікуємо підтвердження продавця.'
                            : order.delivery?.method === 'pickup'
                              ? 'Продавець готує замовлення до особистої передачі.'
                              : 'Продавець готує замовлення до відправки.'
                    }
                    action={
                        <div className="flex flex-wrap gap-2">
                            {order.buyer_editable && (
                                <Button
                                    variant="outlinePrimary"
                                    size="sm"
                                    onClick={() => onEditOrder(order)}
                                >
                                    <Pencil className="mr-1 h-4 w-4" />
                                    Редагувати
                                </Button>
                            )}
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onCancelOrder(order.id)}
                                className="self-start border-red-200 text-red-600 hover:border-red-300 hover:bg-red-50 sm:self-auto dark:bg-red-200/12 dark:text-red-300 dark:hover:bg-red-300/8"
                            >
                                <X className="mr-1 h-4 w-4" />
                                Скасувати замовлення
                            </Button>
                        </div>
                    }
                />
            )}

            {order.status === 'paid' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Clock}
                    message="Замовлення оплачено. Очікуємо обробку."
                    action={
                        <div className="flex flex-wrap gap-2">
                            {order.buyer_editable && (
                                <Button
                                    variant="outlinePrimary"
                                    size="sm"
                                    onClick={() => onEditOrder(order)}
                                >
                                    <Pencil className="mr-1 h-4 w-4" />
                                    Редагувати
                                </Button>
                            )}
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onCancelOrder(order.id)}
                                className="self-start border-red-200 text-red-600 hover:border-red-300 hover:bg-red-50 sm:self-auto"
                            >
                                <X className="mr-1 h-4 w-4" />
                                Скасувати замовлення
                            </Button>
                        </div>
                    }
                />
            )}

            {order.status === 'delivered' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={CheckCircle}
                    message={
                        order.delivery?.method === 'pickup'
                            ? 'Ви підтвердили отримання. Замовлення вже скоро буде завершено, після цього у вас буде 90 днів на відгук.'
                            : 'Перевізник підтвердив отримання. Замовлення буде завершено автоматично, після цього у вас буде 90 днів на відгук.'
                    }
                />
            )}

            {order.status === 'completed' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Package}
                    message={
                        order.review
                            ? 'Замовлення виконано.'
                            : order.can_review
                              ? 'Замовлення виконано! Поділіться враженнями про продавця.'
                              : 'Замовлення виконано.'
                    }
                    action={
                        order.can_review ? (
                            <Button
                                size="sm"
                                variant="outline"
                                className="self-start border-blue-200 text-blue-700 hover:border-blue-300 hover:bg-blue-50 sm:self-auto dark:border-primary/50 dark:bg-primary/15 dark:text-primary-foreground dark:hover:border-primary/60 dark:hover:bg-primary/25 dark:hover:text-white"
                                onClick={() => onReviewOrder(order)}
                            >
                                <MessageSquare className="mr-1 h-4 w-4" />
                                Залишити відгук
                            </Button>
                        ) : undefined
                    }
                />
            )}

            {order.status === 'rejected' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Package}
                    message={
                        <>
                            Продавець відхилив замовлення.
                            <CancellationDetails order={order} />
                        </>
                    }
                />
            )}

            {order.status === 'returned' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Package}
                    message="Відправлення повернено перевізником."
                />
            )}

            {order.status === 'auto_cancelled' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Clock}
                    message="Замовлення автоматично скасовано."
                />
            )}

            {order.status === 'dispute' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Clock}
                    message="За цим замовленням відкрито суперечку."
                />
            )}

            {order.status === 'cancelled' && (
                <OrderStatusNotice
                    tone={tone}
                    icon={Package}
                    message={
                        <>
                            Ви скасували замовлення.
                            <CancellationDetails order={order} />
                        </>
                    }
                />
            )}
        </>
    );
};
